import React, { useMemo } from "react";

import { Preview, TextState } from "@creatomate/preview";
import { Button, Tooltip } from "@culturehq/components";
import { ElementSource } from "chq";

import { useProfile } from "app/context";
import { t, Trans } from "app/utils";
import { createSubtitleElement, getJsonSubtitleLanguages, isSubtitleText, updateSubtitleIndexes } from "../utils";
import getSubtitleVideoRef from "../utils/getSubtitleVideoRef";

const SECTION_DURATION = 0.5;

interface AddSectionProps {
  preview: Preview;
  subtitleElement?: TextState;
}

const AddSection: React.FC<AddSectionProps> = ({ preview, subtitleElement }) => {
  const { profile: { organization } } = useProfile();
  const { elements } = preview.state || { elements: [] };
  const allSubtitleElements = useMemo(() => elements.filter(
    element => isSubtitleText(element.source.name)), [elements]
  );
  const settingsPerLanguage = useMemo(() => (
    allSubtitleElements.reduce<Record<string, ElementSource>>((settings, element) => {
      const [_prefix, _index, language] = element.source.name.split("-", 3);
      if (!settings[language]) {
        return {
          ...settings,
          [language]: element.source
        };
      }
      return settings;
    }, {})
  ), [allSubtitleElements]);

  const getNewIndex = (): number => {
    if (!subtitleElement) return 0;

    const [_prefix, index] = subtitleElement.source.name.split("-", 2);
    return parseInt(index, 10) + 1;
  };

  const handleAddSection = async () => {
    const source = preview.getSource();
    const newIndex = getNewIndex();

    const [_prefix, _index, _language, _id] = subtitleElement?.source?.name?.split("-", 4) || [];
    const [_genrealPrefix, referenceId] = getSubtitleVideoRef(subtitleElement?.source?.name);

    const newSectionStartTime = subtitleElement
      ? subtitleElement.globalTime + subtitleElement.duration + 0.001
      : 0;
    const newSectionEndTime = newSectionStartTime + SECTION_DURATION;

    const languages = getJsonSubtitleLanguages(allSubtitleElements);

    const newSubtitleElements = languages.map(language => (
      createSubtitleElement(
        "New Section",
        newIndex,
        newSectionStartTime,
        newSectionEndTime,
        settingsPerLanguage[language].track,
        organization.name || "",
        referenceId,
        language,
        subtitleElement,
        undefined,
        settingsPerLanguage[language].visible
      )
    ));

    const noSubtitleElements = source.elements.filter((element: ElementSource) => (
      !isSubtitleText(element.name)
    ));
    const subtitleElements = source.elements.filter((element: ElementSource) => (
      isSubtitleText(element.name)
    ));
    const reindexedElements = updateSubtitleIndexes(
      [...subtitleElements, ...newSubtitleElements]
    );

    await preview.setSource({
      ...source,
      elements: [...noSubtitleElements, ...reindexedElements]
    }, true);
  };

  if (!subtitleElement) {
    return (
      <Button className="Add-Section" icon="plus" inverted onClick={handleAddSection}>
        <Trans>Add section</Trans>
      </Button>
    );
  }

  return (
    <Tooltip tip={t("Add a new section below")}>
      <Button className="Add-Section" icon="plus" inverted onClick={handleAddSection} />
    </Tooltip>
  );
};

export default AddSection;
